import React from 'react';
import { Address, MapboxFeature } from '../types';

interface AddressModalProps {
  clientId?: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onEdit: (e: React.FormEvent) => void;
  newAddress: Address;
  addressErrors: { street: string; city: string; state: string; province: string; zipCode: string; server: string };
  editingAddressId: number | null;
  addressSuggestions: MapboxFeature[];
  showSuggestions: boolean;
  autocompleteInputRef: React.RefObject<HTMLInputElement | null>; // Temporary workaround
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddressSelect: (feature: MapboxFeature) => void;
}

const AddressModal = ({
  onClose,
  onSubmit,
  onEdit,
  newAddress,
  addressErrors,
  editingAddressId,
  addressSuggestions,
  showSuggestions,
  autocompleteInputRef,
  handleInputChange,
  handleAddressSelect,
}: AddressModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">{editingAddressId ? 'Edit Address' : 'Add New Address'}</h3>
        <form onSubmit={editingAddressId ? onEdit : onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                Street
              </label>
              <input
                type="text"
                id="street"
                name="street"
                ref={autocompleteInputRef}
                value={newAddress.street}
                onChange={handleInputChange}
                onFocus={() => newAddress.street && handleInputChange({ target: { name: 'street', value: newAddress.street } } as any)}
                autoComplete="new-address"
                className={`w-full px-3 py-2 border ${
                  addressErrors.street ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. 123 Main St"
              />
              {addressErrors.street && <p className="text-red-500 text-xs mt-1">{addressErrors.street}</p>}
              {showSuggestions && addressSuggestions.length > 0 ? (
                <ul className="absolute z-[100] w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                  {addressSuggestions.map((suggestion) => (
                    <li
                      key={suggestion.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onClick={() => handleAddressSelect(suggestion)}
                    >
                      {suggestion.place_name}
                    </li>
                  ))}
                </ul>
              ) : (
                showSuggestions && (
                  <p className="absolute z-[100] w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 px-4 py-2 text-sm text-gray-500">
                    No suggestions found
                  </p>
                )
              )}
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City/Town
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={newAddress.city}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  addressErrors.city ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. New York"
              />
              {addressErrors.city && <p className="text-red-500 text-xs mt-1">{addressErrors.city}</p>}
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                Suburb
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={newAddress.state}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  addressErrors.state ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. NY"
              />
              {addressErrors.state && <p className="text-red-500 text-xs mt-1">{addressErrors.state}</p>}
            </div>

                <div>
              <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                Province
              </label>
              <input
                type="text"
                id="province"
                name="province"
                value={newAddress.province}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  addressErrors.province ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. KwaZulu-Natal"
              />
              {addressErrors.province && <p className="text-red-500 text-xs mt-1">{addressErrors.province}</p>}
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={newAddress.zipCode}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  addressErrors.zipCode ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. 12345"
              />
              {addressErrors.zipCode && <p className="text-red-500 text-xs mt-1">{addressErrors.zipCode}</p>}
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {editingAddressId ? 'Save Changes' : 'Add Address'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressModal;